// src/lib/merchantMatcher.js
import { PROFESSION_MAP } from './professions.js';

/**
 * Normalize a raw merchant description for substring alias matching.
 * Lowercases, strips punctuation/asterisks (Plaid noise), collapses whitespace.
 */
export function normalize(s) {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function findOverrideKey(normalizedDesc, overrides) {
  // Override keys are themselves normalized merchant names. Match by substring.
  const keys = Object.keys(overrides).sort((a, b) => b.length - a.length);
  return keys.find(key => key && normalizedDesc.includes(key));
}

function resolveOverride(normalizedDesc, store) {
  const overrides = store?.overrides || {};
  const key = findOverrideKey(normalizedDesc, overrides);
  if (!key) return null;
  const o = overrides[key];
  if (!o) return null;
  if (o.ignored) {
    return {
      merchantName: null,
      category: null,
      deductible: null,
      splitRule: null,
      confidence: 'none',
      source: 'override',
    };
  }
  if (!o.category) return null; // corrupt entry (no category, not ignored) → fall through
  const isHigh = o.autoConfirmed === true || (o.confirmationCount ?? 0) >= 3;
  return {
    merchantName: key,
    category: o.category,
    deductible: o.deductible ?? null,
    splitRule: null, // attached later
    confidence: isHigh ? 'high' : 'medium',
    source: 'override',
  };
}

/**
 * Resolve a merchant match for a transaction description.
 * @param {string} rawDescription - raw merchant text (e.g. Plaid memo)
 * @param {string|null} profession - profession key (e.g. 'beauty')
 * @param {{overrides:object, splitRules:object}} store - merchantStore state slice
 * @returns {MatchResult}
 */
export function matchMerchant(rawDescription, profession, store) {
  const desc = normalize(rawDescription);
  if (!desc) return noMatch();

  // 1. Override resolution
  const override = resolveOverride(desc, store);
  if (override) return attachSplitRule(override, store);

  // 2. Seed resolution
  const seed = resolveSeed(desc, profession);
  if (seed) return attachSplitRule(seed, store);

  // 3. No match
  return noMatch();
}

function resolveSeed(normalizedDesc, profession) {
  if (!profession) return null;
  const config = PROFESSION_MAP[profession];
  if (!config || !Array.isArray(config.commonMerchants)) return null;

  // Build flat (merchant, alias) pairs sorted by alias length desc, so the
  // longest alias hits before any shorter alias from a different merchant.
  const pairs = [];
  for (const m of config.commonMerchants) {
    const aliases = Array.isArray(m.aliases) && m.aliases.length > 0
      ? m.aliases
      : [normalize(m.name)]; // fallback: canonical name
    for (const alias of aliases) {
      pairs.push({ merchant: m, alias });
    }
  }
  pairs.sort((a, b) => b.alias.length - a.alias.length);

  const hit = pairs.find(p => p.alias && normalizedDesc.includes(p.alias));
  if (!hit) return null;

  return {
    merchantName: hit.merchant.name,
    category: hit.merchant.category,
    deductible: hit.merchant.deductible,
    splitRule: null,
    confidence: hit.merchant.seedConfidence,
    source: 'seed',
  };
}

function noMatch() {
  return {
    merchantName: null,
    category: null,
    deductible: null,
    splitRule: null,
    confidence: 'none',
    source: 'none',
  };
}

function attachSplitRule(result, store) {
  const rules = store?.splitRules || {};
  if (!result.merchantName) return result;
  const key = normalize(result.merchantName);
  if (rules[key]) {
    return { ...result, splitRule: rules[key] };
  }
  return result;
}
